import { useEffect, useRef, useState } from "react"
import { Alert, Button, Card, Icon, Spinner } from "../../../components"
import { Api } from "../../../api/Assinafy"
import { calendarDateFormat, formatBRL } from "../../../utils/Formats"
import { getPixImageSource } from "../pixPayment"

import "./styles.scss"

const PixPendingPayment = ({
  payment,
  planName,
  processing = false,
  onUnavailable,
  secondaryLabel,
  onSecondary,
  secondaryDisabled = false,
}) => {
  const [pix, setPix] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)
  const copiedTimeoutRef = useRef(null)
  const onUnavailableRef = useRef(onUnavailable)

  useEffect(() => {
    onUnavailableRef.current = onUnavailable
  }, [onUnavailable])

  useEffect(() => {
    let ignore = false

    const fetchPix = async () => {
      setLoading(true)
      setError(null)

      try {
        const response = await Api("subscription/pendingPix")()
        if (!ignore && response?.status === 200 && response?.data?.data) {
          setPix(response.data.data)
        }
      } catch (requestError) {
        if (ignore) return

        if (requestError?.response?.status === 404) {
          setError("Este PIX não está mais disponível. Estamos atualizando a situação da compra.")
          onUnavailableRef.current?.()
        } else {
          setError("Não foi possível carregar o QR Code agora. Tente novamente em instantes.")
        }
      } finally {
        if (!ignore) setLoading(false)
      }
    }

    fetchPix()

    return () => {
      ignore = true
      if (copiedTimeoutRef.current) window.clearTimeout(copiedTimeoutRef.current)
    }
  }, [payment?.id])

  const copyPayload = async () => {
    if (!pix?.payload) return

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(pix.payload)
      } else {
        const input = document.createElement("textarea")
        input.value = pix.payload
        input.setAttribute("readonly", "")
        input.style.position = "fixed"
        input.style.opacity = "0"
        document.body.appendChild(input)
        input.select()
        document.execCommand("copy")
        document.body.removeChild(input)
      }

      setCopied(true)
      if (copiedTimeoutRef.current) window.clearTimeout(copiedTimeoutRef.current)
      copiedTimeoutRef.current = window.setTimeout(() => setCopied(false), 2500)
    } catch (_) {
      setError("Não foi possível copiar automaticamente. Selecione o código abaixo e copie manualmente.")
    }
  }

  const amount = pix?.amount ?? payment?.amount
  const dueDate = pix?.due_date ?? payment?.due_date
  const imageSource = getPixImageSource(pix?.encoded_image)

  return (
    <Card className="pix-payment-card">
      <div className="pix-payment-card__header">
        <div className="pix-payment-card__icon"><Icon id="icon-qr_code_scanner" size="28" /></div>
        <div>
          <p className="pix-payment-card__eyebrow">Pagamento pendente</p>
          <h2>PIX do plano {planName || "selecionado"}</h2>
          <p>
            {formatBRL(amount)} até {calendarDateFormat(dueDate)}. Seu plano atual continua ativo até a confirmação do pagamento.
          </p>
        </div>
      </div>

      {processing && (
        <Alert variant="info">Pagamento recebido. Estamos ativando o novo plano e atualizaremos esta tela automaticamente.</Alert>
      )}

      {loading ? (
        <div className="pix-payment-card__loading" aria-live="polite">
          <Spinner size={40} />
          <span>Carregando QR Code...</span>
        </div>
      ) : (
        <>
          {error && <Alert variant="warning">{error}</Alert>}
          {pix && (
            <div className="pix-payment-card__content">
              {imageSource && (
                <img className="pix-payment-card__qr" src={imageSource} alt="QR Code PIX para pagamento do plano" />
              )}
              <div className="pix-payment-card__copy">
                <strong>PIX Copia e Cola</strong>
                <p>Copie o código e pague pelo aplicativo do seu banco.</p>
                <div className="pix-payment-card__payload" tabIndex="0">{pix.payload}</div>
                <Button variant="primary" onClick={copyPayload} disabled={!pix.payload}>
                  <Icon id={copied ? "icon-check" : "icon-copy"} size="18" />
                  {copied ? "Código copiado" : "Copiar código PIX"}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <div className="pix-payment-card__actions">
        {secondaryLabel && onSecondary && (
          <Button variant="border" onClick={onSecondary} disabled={secondaryDisabled || processing}>
            {secondaryLabel}
          </Button>
        )}
      </div>
    </Card>
  )
}

export default PixPendingPayment
